import { useState, useMemo, useRef } from "react";
import { X, LogIn, Eye, EyeOff, Bell, Loader2, ArrowLeft, Mail, Phone, Check, KeyRound, Shield } from "lucide-react";
import { useTranslation } from "react-i18next";
import { registerFcmToken } from "../../firebase";
import { useDraggable } from "../../hooks/useDraggable";
import { useEscClose } from "../../hooks/useEscClose";
import { getRecaptchaToken } from "../../hooks/useRecaptcha";
import { usePopupTopOffset } from "../../hooks/usePopupTopOffset";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";

interface LoginPopupProps {
  onClose: () => void;
  onSwitchToSignup?: () => void;
}

type Mode = "login" | "forgot" | "forgot-code" | "forgot-newpw" | "forgot-done";

export default function LoginPopup({ onClose, onSwitchToSignup }: LoginPopupProps) {
  const { t } = useTranslation();
  const [closing, setClosing] = useState(false);
  const [showNotifPrompt, setShowNotifPrompt] = useState(false);
  const { position, onMouseDown, dragged, prepareClose } = useDraggable();
  const popupTop = usePopupTopOffset();

  // Mode
  const [mode, setMode] = useState<Mode>("login");

  // Login state
  const [uid, setUid] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Forgot password state
  const [forgotMethod, setForgotMethod] = useState<"EMAIL" | "PHONE">("EMAIL");
  const [forgotIdentifier, setForgotIdentifier] = useState("");
  const [forgotCode, setForgotCode] = useState("");
  const [forgotNewPw, setForgotNewPw] = useState("");
  const [forgotConfirmPw, setForgotConfirmPw] = useState("");
  const [showNewPw, setShowNewPw] = useState(false);
  const [codeSending, setCodeSending] = useState(false);
  const [codeVerifying, setCodeVerifying] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || "https://api.trippang.com";

  // Password strength
  const pwStrength = useMemo(() => {
    if (!forgotNewPw) return 0;
    let s = 0;
    if (forgotNewPw.length >= 8) s++;
    if (/[A-Z]/.test(forgotNewPw)) s++;
    if (/[0-9]/.test(forgotNewPw)) s++;
    if (/[^A-Za-z0-9]/.test(forgotNewPw)) s++;
    return s;
  }, [forgotNewPw]);

  const pwStrengthLabel = ["", t("signup.pwWeak"), t("signup.pwFair"), t("signup.pwGood"), t("signup.pwStrong")];
  const pwStrengthColor = ["", "bg-red-400", "bg-orange-300", "bg-amber-400", "bg-green-400"];

  const handleClose = () => {
    prepareClose(); setClosing(true);
    setTimeout(onClose, 400);
  };

  useEscClose(handleClose);
  const containerRef = useRef<HTMLDivElement>(null);
  useBodyScrollLock(containerRef);

  const switchToForgot = () => {
    setMode("forgot");
    setError("");
    setForgotMethod("EMAIL");
    setForgotIdentifier("");
    setForgotCode("");
    setForgotNewPw("");
    setForgotConfirmPw("");
  };

  const backToLogin = () => {
    setMode("login");
    setError("");
  };

  // ── Login ──
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!uid.trim()) { setError(t("login.validIdentifier")); return; }
    if (!password.trim()) { setError(t("login.validPassword")); return; }

    setLoading(true);
    try {
      // recaptcha: 5초 timeout (서비스 hang 방어)
      let recaptchaToken = "";
      try {
        recaptchaToken = (await Promise.race([
          getRecaptchaToken("signin"),
          new Promise<string>((_, rej) => setTimeout(() => rej(new Error("recaptcha-timeout")), 5000)),
        ])) as string;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[login] recaptcha skip:", e);
      }

      // signin fetch: 15초 timeout (네트워크 hang 방어)
      const ctrl = new AbortController();
      const tid = setTimeout(() => ctrl.abort(), 15000);
      let res: Response;
      try {
        res = await fetch(`${API_URL}/user/auth/signin`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ identifier: uid, upw: password, recaptcha_token: recaptchaToken }),
          signal: ctrl.signal,
        });
      } finally {
        clearTimeout(tid);
      }
      const data = await res.json();

      if (data.status !== "success") {
        if (data.detail && Array.isArray(data.detail)) {
          setError(data.detail[0]?.msg || t("msg.NETWORK_ERROR"));
        } else {
          setError(t("msg." + data.code) || data.code || t("msg.NETWORK_ERROR"));
        }
        return;
      }

      localStorage.setItem("access_token", data.data.access_token);
      localStorage.setItem("refresh_token", data.data.refresh_token);
      localStorage.setItem("user_id", String(data.data.user_id));
      localStorage.setItem("uid", data.data.uid);
      localStorage.setItem("nick_name", data.data.nick_name || data.data.uid);
      if (data.data.session_id) localStorage.setItem("session_id", String(data.data.session_id));
      localStorage.setItem("favorite_country_id", String(data.data.favorite_country_id || 1));
      if (data.data.favorite_city_id) localStorage.setItem("favorite_city_id", String(data.data.favorite_city_id));
      if (data.data.language_id) localStorage.setItem("language_id", String(data.data.language_id));

      try {
        // 요구사항 #475: denied(차단)도 default(미결정)와 동일하게 7일 1회로 억제.
        // (예전엔 denied만 매번 노출 — 크롬에서 한 번 차단되면 로그인마다 안내가 반복 표시되던 문제)
        if (
          typeof Notification !== "undefined" &&
          (Notification.permission === "denied" || Notification.permission === "default")
        ) {
          const dismissedAt = parseInt(localStorage.getItem("notif_prompt_dismissed_at") || "0", 10);
          const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;
          if (!dismissedAt || Date.now() - dismissedAt > SEVEN_DAYS) {
            setShowNotifPrompt(true);
            return;
          }
        }
        // granted/denied/undefined 모두 동일: FCM 은 fire-and-forget 으로 reload 후 App.tsx 가 처리
        if (typeof Notification !== "undefined" && Notification.permission === "granted") {
          registerFcmToken().catch(() => {});
        }
      } catch {}
      // reload 대신 auth-changed → Header/컨텍스트가 로그인 상태 즉시 반영 + 팝업 닫기 (화면 튕김 제거)
      window.dispatchEvent(new CustomEvent("auth-changed"));
      handleClose();
    } catch {
      setError(t("msg.NETWORK_ERROR"));
    } finally {
      setLoading(false);
    }
  };

  // ── Forgot: Send Code ──
  const handleForgotSendCode = async () => {
    if (!forgotIdentifier.trim()) {
      setError(forgotMethod === "EMAIL" ? t("pw.validEmail") : t("signup.validPhone"));
      return;
    }
    setError("");
    setCodeSending(true);
    try {
      let recaptchaToken = "";
      try { recaptchaToken = await getRecaptchaToken("send_code"); } catch {}
      const res = await fetch(`${API_URL}/user/auth/send-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier: forgotIdentifier, method: forgotMethod, recaptcha_token: recaptchaToken }),
      });
      const data = await res.json();
      if (data.status === "success") {
        setMode("forgot-code");
        setError("");
      } else {
        setError(t("msg." + data.code) || data.code);
      }
    } catch { setError(t("msg.NETWORK_ERROR")); }
    finally { setCodeSending(false); }
  };

  // ── Forgot: Verify Code → go to new password ──
  const handleForgotVerify = async () => {
    if (forgotCode.length !== 6) return;
    setError("");
    setCodeVerifying(true);
    try {
      const res = await fetch(`${API_URL}/user/auth/verify-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier: forgotIdentifier, code: forgotCode }),
      });
      const data = await res.json();
      if (data.status === "success") {
        setMode("forgot-newpw");
        setError("");
      } else {
        setError(t("msg." + data.code) || data.code);
      }
    } catch { setError(t("msg.NETWORK_ERROR")); }
    finally { setCodeVerifying(false); }
  };

  // ── Forgot: Reset Password ──
  const handleResetPassword = async () => {
    setError("");
    if (!forgotNewPw.trim() || forgotNewPw.length < 8) { setError(t("signup.validPasswordLength")); return; }
    if (forgotNewPw !== forgotConfirmPw) { setError(t("pw.mismatch")); return; }

    setLoading(true);
    try {
      let recaptchaToken = "";
      try { recaptchaToken = await getRecaptchaToken("reset_password"); } catch {}
      const res = await fetch(`${API_URL}/user/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          identifier: forgotIdentifier,
          code: forgotCode,
          new_password: forgotNewPw,
          recaptcha_token: recaptchaToken,
        }),
      });
      const data = await res.json();
      if (data.status === "success") {
        setMode("forgot-done");
      } else {
        setError(t("msg." + data.code) || data.code);
      }
    } catch { setError(t("msg.NETWORK_ERROR")); }
    finally { setLoading(false); }
  };

  const inputClass =
    "w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-1 text-sm text-slate-900 placeholder-slate-400 outline-none transition-all focus:border-amber-400/50 focus:bg-slate-100 focus:ring-1 focus:ring-amber-400/30";

  return (
    <div ref={containerRef} className="fixed left-0 right-0 bottom-0 z-[120] flex items-center justify-center" style={{ top: popupTop }} onClick={handleClose}>
      {/* Overlay */}
      <div
        className="absolute inset-0"
        style={{ animation: closing ? "flipOverlayIn 0.4s ease reverse forwards" : "flipOverlayIn 0.3s ease forwards" }}
      />

      {/* Popup */}
      <div
        className="relative z-10 mx-4 w-full max-w-sm overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl shadow-black/50"
        style={{
          animation: dragged ? "none" : (closing ? "slideDownOut 0.4s ease forwards" : "slideBounceDown 0.9s ease forwards"),
          transform: `translate(${position.x}px, ${position.y}px)`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">

          {/* ═══════ LOGIN MODE ═══════ */}
          {mode === "login" && (
            <>
              {/* Header */}
              <div className="pb-3 mb-4 border-b border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-2.5" onMouseDown={onMouseDown} style={{ cursor: "move" }}>
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-amber-400 to-orange-300">
                    <LogIn className="h-4 w-4 text-white" />
                  </div>
                  <h2 className="text-lg font-bold text-slate-900">{t("login.title")}</h2>
                </div>
                <button onClick={handleClose} className="rounded-full p-1.5 text-red-400 transition-colors hover:bg-red-500/10 hover:text-red-300">
                  <X className="h-5 w-5" strokeWidth={2.5} />
                </button>
              </div>

              <form onSubmit={handleLogin} className="space-y-4">
                {error && (
                  <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-xs text-red-400">{error}</div>
                )}

                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-sky-600">{t("login.identifier")}</label>
                  <input type="text" value={uid} onChange={(e) => setUid(e.target.value)}
                    placeholder={t("login.identifierPlaceholder")} className={inputClass} autoFocus
                    autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="email" />
                </div>

                <div>
                  <div className="mb-1.5 flex items-center justify-between">
                    <label className="text-xs font-semibold text-sky-600">{t("login.password")}</label>
                    <button type="button" onClick={switchToForgot} className="text-xs font-semibold text-yellow-500 transition-colors hover:text-yellow-600 hover:underline">
                      {t("login.forgotPassword")}
                    </button>
                  </div>
                  <div className="relative">
                    <input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                      placeholder={t("login.passwordPlaceholder")} className={`${inputClass} pr-10`} />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 transition-colors hover:text-slate-700">
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <button type="submit" disabled={loading}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-300 py-2.5 text-sm font-bold text-slate-900 shadow-lg shadow-amber-500/25 transition-all hover:shadow-amber-500/40 hover:brightness-110 active:scale-[0.98] disabled:opacity-50">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
                  {loading ? t("login.loading") : t("login.title")}
                </button>

              </form>

              {/* Signup link */}
              <div className="mt-5 text-center text-xs text-slate-500">
                {t("login.noAccount")}{" "}
                <button onClick={() => { handleClose(); if (onSwitchToSignup) setTimeout(onSwitchToSignup, 500); }}
                  className="font-semibold text-yellow-500 transition-colors hover:text-yellow-600 hover:underline">
                  {t("login.signup")}
                </button>
              </div>
            </>
          )}

          {/* ═══════ FORGOT: Email Input ═══════ */}
          {mode === "forgot" && (
            <>
              <div className="pb-3 mb-4 border-b border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <button onClick={backToLogin} className="rounded-full p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-900">
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                  <h2 className="text-lg font-bold text-slate-900">{t("pw.forgotTitle")}</h2>
                </div>
                <button onClick={handleClose} className="rounded-full p-1.5 text-red-400 transition-colors hover:bg-red-500/10 hover:text-red-300">
                  <X className="h-5 w-5" strokeWidth={2.5} />
                </button>
              </div>

              <div className="space-y-4">
                {error && <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-xs text-red-400">{error}</div>}

                <p className="text-sm text-slate-600">{t("pw.forgotDesc")}</p>

                {/* Method Tabs */}
                <div className="flex gap-2">
                  <button type="button" onClick={() => { setForgotMethod("EMAIL"); setForgotIdentifier(""); setError(""); }}
                    className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl py-2.5 text-sm font-semibold transition-all ${
                      forgotMethod === "EMAIL"
                        ? "bg-gradient-to-r from-amber-500 to-orange-300 text-white shadow-lg shadow-amber-500/20"
                        : "border border-slate-200 bg-slate-50 text-slate-500 hover:text-slate-700"
                    }`}>
                    <Mail className="h-4 w-4" />
                    {t("signup.email")}
                  </button>
                  <button type="button" onClick={() => { setForgotMethod("PHONE"); setForgotIdentifier(""); setError(""); }}
                    className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl py-2.5 text-sm font-semibold transition-all ${
                      forgotMethod === "PHONE"
                        ? "bg-gradient-to-r from-amber-500 to-orange-300 text-white shadow-lg shadow-amber-500/20"
                        : "border border-slate-200 bg-slate-50 text-slate-500 hover:text-slate-700"
                    }`}>
                    <Phone className="h-4 w-4" />
                    {t("signup.phone")}
                  </button>
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-sky-600">
                    {forgotMethod === "EMAIL" ? t("signup.email") : t("signup.phone")}
                  </label>
                  {forgotMethod === "EMAIL" ? (
                    <input type="email" value={forgotIdentifier} onChange={(e) => setForgotIdentifier(e.target.value)}
                      placeholder="name@example.com" className={inputClass} autoFocus />
                  ) : (
                    <input type="tel" value={forgotIdentifier}
                      onChange={(e) => setForgotIdentifier(e.target.value.replace(/\D/g, ""))}
                      placeholder="+639171234567" className={inputClass} />
                  )}
                </div>

                <button type="button" onClick={handleForgotSendCode} disabled={codeSending || !forgotIdentifier.trim()}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-300 py-2.5 text-sm font-bold text-slate-900 shadow-lg shadow-amber-500/25 transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-50">
                  {codeSending ? <Loader2 className="h-4 w-4 animate-spin" /> : forgotMethod === "EMAIL" ? <Mail className="h-4 w-4" /> : <Phone className="h-4 w-4" />}
                  {codeSending ? t("signup.loading") : t("signup.sendCode")}
                </button>
              </div>
            </>
          )}

          {/* ═══════ FORGOT: Code Input ═══════ */}
          {mode === "forgot-code" && (
            <>
              <div className="pb-3 mb-4 border-b border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <button onClick={() => setMode("forgot")} className="rounded-full p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-900">
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                  <h2 className="text-lg font-bold text-slate-900">{t("pw.forgotTitle")}</h2>
                </div>
                <button onClick={handleClose} className="rounded-full p-1.5 text-red-400 transition-colors hover:bg-red-500/10 hover:text-red-300">
                  <X className="h-5 w-5" strokeWidth={2.5} />
                </button>
              </div>

              <div className="space-y-4">
                {error && <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-xs text-red-400">{error}</div>}

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-center">
                  <div className={`mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full ${forgotMethod === "EMAIL" ? "bg-sky-500/15" : "bg-green-500/15"}`}>
                    {forgotMethod === "EMAIL" ? <Mail className="h-5 w-5 text-sky-400" /> : <Phone className="h-5 w-5 text-green-400" />}
                  </div>
                  <p className="text-xs text-slate-600">{t("pw.codeSentTo")}</p>
                  <p className="text-sm font-semibold text-slate-900">{forgotIdentifier}</p>
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-sky-600">{t("signup.codePlaceholder")}</label>
                  <input type="text" value={forgotCode}
                    onChange={(e) => setForgotCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    maxLength={6} autoFocus
                    className={`${inputClass} text-center font-mono text-lg tracking-[0.3em]`} />
                </div>

                <button type="button" onClick={handleForgotVerify} disabled={codeVerifying || forgotCode.length !== 6}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-300 py-2.5 text-sm font-bold text-slate-900 shadow-lg shadow-amber-500/25 transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-50">
                  {codeVerifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Shield className="h-4 w-4" />}
                  {codeVerifying ? t("signup.loading") : t("signup.verifyCode")}
                </button>

                <button type="button" onClick={handleForgotSendCode} disabled={codeSending}
                  className="w-full text-center text-xs text-slate-500 underline transition-colors hover:text-slate-700">
                  {t("signup.resendCode")}
                </button>
              </div>
            </>
          )}

          {/* ═══════ FORGOT: New Password ═══════ */}
          {mode === "forgot-newpw" && (
            <>
              <div className="pb-3 mb-4 border-b border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-amber-400 to-orange-300">
                    <KeyRound className="h-4 w-4 text-white" />
                  </div>
                  <h2 className="text-lg font-bold text-slate-900">{t("pw.newTitle")}</h2>
                </div>
                <button onClick={handleClose} className="rounded-full p-1.5 text-red-400 transition-colors hover:bg-red-500/10 hover:text-red-300">
                  <X className="h-5 w-5" strokeWidth={2.5} />
                </button>
              </div>

              <div className="space-y-4">
                {error && <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-xs text-red-400">{error}</div>}

                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-sky-600">{t("pw.new")}</label>
                  <div className="relative">
                    <input type={showNewPw ? "text" : "password"} value={forgotNewPw} onChange={(e) => setForgotNewPw(e.target.value)}
                      placeholder={t("signup.passwordHint")} className={`${inputClass} pr-10`} autoFocus />
                    <button type="button" onClick={() => setShowNewPw(!showNewPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 transition-colors hover:text-slate-700">
                      {showNewPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {forgotNewPw && (
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex flex-1 gap-1">
                        {[1, 2, 3, 4].map((i) => (
                          <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i <= pwStrength ? pwStrengthColor[pwStrength] : "bg-slate-200"}`} />
                        ))}
                      </div>
                      <span className={`text-[10px] font-semibold ${
                        pwStrength <= 1 ? "text-red-400" : pwStrength === 2 ? "text-orange-300" : pwStrength === 3 ? "text-amber-400" : "text-green-400"
                      }`}>{pwStrengthLabel[pwStrength]}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-sky-600">{t("pw.confirm")}</label>
                  <input type="password" value={forgotConfirmPw} onChange={(e) => setForgotConfirmPw(e.target.value)}
                    placeholder={t("pw.confirmPlaceholder")} className={inputClass} />
                  {forgotConfirmPw && forgotNewPw && forgotConfirmPw !== forgotNewPw && (
                    <p className="mt-1.5 text-xs text-red-400">{t("pw.mismatch")}</p>
                  )}
                </div>

                <button type="button" onClick={handleResetPassword} disabled={loading}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-300 py-2.5 text-sm font-bold text-slate-900 shadow-lg shadow-amber-500/25 transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-50">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                  {loading ? t("pw.changing") : t("pw.reset")}
                </button>
              </div>
            </>
          )}

          {/* ═══════ FORGOT: Done ═══════ */}
          {mode === "forgot-done" && (
            <div className="flex flex-col items-center py-8 text-center">
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-500/20">
                <Check className="h-7 w-7 text-green-400" />
              </div>
              <p className="text-lg font-bold text-slate-900">{t("pw.resetDone")}</p>
              <p className="mt-2 text-sm text-slate-600">{t("pw.resetDoneDesc")}</p>
              <button onClick={backToLogin}
                className="mt-6 flex items-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-300 px-6 py-2.5 text-sm font-bold text-slate-900 shadow-lg shadow-amber-500/25 transition-all hover:brightness-110 active:scale-[0.98]">
                <LogIn className="h-4 w-4" />
                {t("login.title")}
              </button>
            </div>
          )}

        </div>
      </div>

      {/* Notification permission prompt */}
      {showNotifPrompt && (
        <div className="fixed left-0 right-0 bottom-0 z-[200] flex items-center justify-center" style={{ top: popupTop }}>
          <div className="mx-4 w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-2xl">
            <div className="flex justify-center mb-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-500/15">
                <Bell className="h-7 w-7 text-amber-400" />
              </div>
            </div>
            {typeof Notification !== "undefined" && Notification.permission === "denied" ? (
              /* 차단(denied) — 브라우저가 재요청을 막으므로 앱에서 켜는 법 안내 */
              <>
                <h3 className="text-base font-bold text-slate-900 mb-2">{t("notif.blocked_title")}</h3>
                <p className="text-xs text-slate-500 mb-5 leading-relaxed whitespace-pre-line text-left">{t("notif.blocked_desc")}</p>
                <button onClick={() => {
                    localStorage.setItem("notif_prompt_dismissed_at", String(Date.now()));
                    setShowNotifPrompt(false);
                    window.dispatchEvent(new CustomEvent("auth-changed"));
                    handleClose();
                  }}
                  className="w-full rounded-xl bg-gradient-to-r from-amber-500 to-orange-300 px-4 py-2.5 text-sm font-bold text-slate-900 shadow-lg shadow-amber-500/25 transition-all hover:brightness-110">
                  {t("notif.blocked_ok")}
                </button>
              </>
            ) : (
              <>
                <h3 className="text-base font-bold text-slate-900 mb-2">{t("notif.prompt_title")}</h3>
                <p className="text-xs text-slate-400 mb-6 leading-relaxed">{t("notif.prompt_desc")}</p>
                <div className="flex gap-3">
                  <button onClick={() => {
                      localStorage.setItem("notif_prompt_dismissed_at", String(Date.now()));
                      setShowNotifPrompt(false);
                      window.dispatchEvent(new CustomEvent("auth-changed"));
                      handleClose();
                    }}
                    className="flex-1 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-400 transition-all hover:bg-slate-100 hover:text-slate-900">
                    {t("notif.later")}
                  </button>
                  <button onClick={async () => {
                      localStorage.setItem("notif_prompt_dismissed_at", String(Date.now()));
                      try {
                        await Promise.race([
                          registerFcmToken(),
                          new Promise((resolve) => setTimeout(resolve, 3000)),
                        ]);
                      } catch {}
                      setShowNotifPrompt(false);
                      window.dispatchEvent(new CustomEvent("auth-changed"));
                      handleClose();
                    }}
                    className="flex-1 rounded-xl bg-gradient-to-r from-amber-500 to-orange-300 px-4 py-2.5 text-sm font-bold text-slate-900 shadow-lg shadow-amber-500/25 transition-all hover:brightness-110">
                    {t("notif.allow")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
